/**
 * RewardAddressOverrideManager - Manual XRPL -> reward address mapping for unregistered holders
 */

import React, { useState } from 'react';
import { Button } from '../../ui/components/Button';
import { TextField } from '../../ui/components/TextField';
import { FeatherPlus, FeatherTrash2 } from '@subframe/core';
import {
  useAddressOverrides,
  useCreateAddressOverride,
  useDeleteAddressOverride,
} from '../../hooks/useDistribution';

interface RewardAddressOverrideManagerProps {
  projectSlug: string;
}

export const RewardAddressOverrideManager: React.FC<RewardAddressOverrideManagerProps> = ({ projectSlug }) => {
  const { data, isLoading } = useAddressOverrides(projectSlug);
  const createMutation = useCreateAddressOverride(projectSlug);
  const deleteMutation = useDeleteAddressOverride(projectSlug);

  const [showForm, setShowForm] = useState(false);
  const [xrplAddress, setXrplAddress] = useState('');
  const [rewardAddress, setRewardAddress] = useState('');
  const [label, setLabel] = useState('');

  const overrides = data?.overrides || [];

  const resetForm = () => {
    setXrplAddress('');
    setRewardAddress('');
    setLabel('');
    setShowForm(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!xrplAddress || !rewardAddress) return;

    createMutation.mutate(
      { xrpl_address: xrplAddress, reward_address: rewardAddress, label: label || undefined },
      { onSuccess: resetForm }
    );
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <span className="text-body-bold font-body-bold text-default-font">Address Overrides</span>
        {!showForm && (
          <Button variant="brand-secondary" size="small" icon={<FeatherPlus />} onClick={() => setShowForm(true)}>
            Add Override
          </Button>
        )}
      </div>

      <p className="text-caption font-caption text-subtext-color">
        Manually map XRPL addresses to reward addresses for holders who haven't registered on the platform.
      </p>

      {showForm && (
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 rounded border border-solid border-neutral-border p-4">
          <TextField className="h-auto w-full" label="XRPL Address">
            <TextField.Input
              placeholder="rABC...XYZ"
              value={xrplAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setXrplAddress(e.target.value)}
            />
          </TextField>
          <TextField className="h-auto w-full" label="Reward Address">
            <TextField.Input
              placeholder="Coreum or other reward address"
              value={rewardAddress}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRewardAddress(e.target.value)}
            />
          </TextField>
          <TextField className="h-auto w-full" label="Label (optional)">
            <TextField.Input
              placeholder="e.g., Whale holder #42"
              value={label}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLabel(e.target.value)}
            />
          </TextField>
          {createMutation.error && (
            <span className="text-caption font-caption text-red-600">{createMutation.error.message}</span>
          )}
          <div className="flex gap-2">
            <Button type="submit" variant="brand-primary" size="small" disabled={createMutation.isPending}>Add</Button>
            <Button variant="neutral-secondary" size="small" onClick={resetForm}>Cancel</Button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-subtext-color py-4">Loading overrides...</div>
      ) : overrides.length === 0 ? (
        <div className="text-subtext-color py-4">No address overrides configured.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-body font-body">
            <thead>
              <tr className="border-b border-neutral-border text-left text-caption font-caption text-subtext-color">
                <th className="px-2 py-1">XRPL Address</th>
                <th className="px-2 py-1">Reward Address</th>
                <th className="px-2 py-1">Label</th>
                <th className="px-2 py-1 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {overrides.map(ovr => (
                <tr key={ovr.id} className="border-b border-neutral-border">
                  <td className="px-2 py-1 font-mono text-caption text-default-font">
                    {ovr.xrpl_address.slice(0, 8)}...{ovr.xrpl_address.slice(-6)}
                  </td>
                  <td className="px-2 py-1 font-mono text-caption text-default-font">
                    {ovr.reward_address.slice(0, 10)}...{ovr.reward_address.slice(-6)}
                  </td>
                  <td className="px-2 py-1 text-subtext-color">{ovr.label || '-'}</td>
                  <td className="px-2 py-1">
                    <button onClick={() => deleteMutation.mutate(ovr.id)} className="p-1 rounded hover:bg-red-50">
                      <FeatherTrash2 className="w-3.5 h-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
